import cloneDeep from 'lodash.clonedeep';
import get from 'lodash.get';
import set from 'lodash.set';
import toJS from 'shared/components/toJS';
import unset from 'lodash.unset';
import withAsyncId from 'shared/components/withAsyncId';
import { openCancelConfirmModal } from 'redux/domain/App';
import { cancelVerifyZipCode, getIsVerifyingZip, verifyZipCode } from 'redux/domain/FindCustomer';
import { connect } from 'react-redux';
import { getFormAsyncErrors, getFormSyncErrors, getFormValues, reduxForm } from 'redux-form';
import withRouter from '../../shared/utils/WithRouter';
import ApplicationEntry from './ApplicationEntry';
import { PERSONAL_INFORMATION, PERSONAL_REFERENCES } from './constants';

export const makeMapStateToProps = (initialState, initialProps) => {
	const { asyncId } = initialProps;
	const mapStateToProps = state => {
		const settings = state.cache.get('settings');
		const applicantIndicator = state.welcome.get('applicantIndicator');
		const numReferences =
			settings
				? settings.get('fieldAndFooterSettings').ApplicationPageSettings.NumberofReferencesOnCreditApplication
				: 0;
		const references = Array(numReferences)
			.fill()
			.map(() => ({}));
		const findCustomerValues = getFormValues('FindCustomer')(state);
		let initialValues = {};
		if (findCustomerValues !== undefined) {
			initialValues = {
				customerId: findCustomerValues.customerId,
				city: findCustomerValues.city,
				firstName: findCustomerValues.firstName,
				lastName: findCustomerValues.lastName,
				state: findCustomerValues.state,
				address1: findCustomerValues.address1,
				address2: findCustomerValues.address2,
				sessionId: findCustomerValues.sessionId,
				ssn: findCustomerValues.ssn,
				zipCode: findCustomerValues.zipCode,
			};
		}
		return {
			settings,
			initialValues: {
				[PERSONAL_INFORMATION]: {
					hasSavingsAccount: false,
					hasCheckingAccount: false,
					...initialValues,
				},
				[PERSONAL_REFERENCES]: references,
			},
			formAsyncErrors: getFormAsyncErrors('ApplicationEntry')(state),
			formSyncErrors: getFormSyncErrors('ApplicationEntry')(state),
			formValues: getFormValues('ApplicationEntry')(state),
			active: state.form.ApplicationEntry && state.form.ApplicationEntry.active,
			isVerifyingZip: zipCodeFieldPath => getIsVerifyingZip(state, asyncId, zipCodeFieldPath),
			applicantIndicator,
			update: false,
		};
	};
	return mapStateToProps;
};

const mapDispatchToProps = {
	openCancelConfirmModal,
};

export const asyncValidate = async (values, dispatch, props, blurredField) => {
	const { isVerifyingZip } = props;
	const postalLabel = props.settings.get('fieldAndFooterSettings').ApplicationPageSettings.PostalLabel;
	const stateProvinceLabel = props.settings.get('fieldAndFooterSettings').ApplicationPageSettings.StateLabel;
	if (blurredField.endsWith('zipCode')) {
		const blurredFieldValue = get(values, blurredField);
		const adjacentStateField = `${blurredField.split('.')[0]}.state`;
		const stateValue = get(values, adjacentStateField);
		const errors = cloneDeep(props && props.asyncErrors) || {};
		unset(errors, blurredField);
		if (blurredFieldValue) {
			if (isVerifyingZip(blurredField)) {
				cancelVerifyZipCode(props.asyncId, blurredField)(dispatch);
			}
			const zipResponse = await verifyZipCode(
				props.asyncId,
				blurredFieldValue,
				stateValue,
				blurredField,
			)(dispatch);
			if (!zipResponse) {
				if (Object.keys(errors).length > 0) {
					throw errors;
				}
				return;
			}
			if (zipResponse.data.valid === false) {
				set(errors, blurredField, `Invalid ${postalLabel} For ${stateProvinceLabel}`);
			} else {
				set(errors, blurredField);
			}
		}
		if (Object.keys(errors).length > 0) {
			throw errors;
		}
	} else {
		const blurredFieldValue = get(values, blurredField);
		const adjacentZipCodeField = `${blurredField.split('.')[0]}.zipCode`;
		const zipCodeValue = get(values, adjacentZipCodeField);
		const errors = cloneDeep(props && props.asyncErrors) || {};
		unset(errors, blurredField);
		if (blurredFieldValue) {
			if (isVerifyingZip(blurredField)) {
				cancelVerifyZipCode(props.asyncId, blurredField)(dispatch);
			}
			const zipResponse = await verifyZipCode(
				props.asyncId,
				zipCodeValue,
				blurredFieldValue,
				blurredField,
			)(dispatch);
			if (!zipResponse) {
				if (Object.keys(errors).length > 0) {
					throw errors;
				}
				return;
			}
			if (zipResponse.data.valid === false) {
				set(errors, adjacentZipCodeField, `Invalid ${postalLabel} For ${stateProvinceLabel}`);
			} else {
				set(errors, adjacentZipCodeField);
			}
		}
		if (Object.keys(errors).length > 0) {
			throw errors;
		}
	}
};

export const shouldAsyncValidate = ({ trigger, blurredField }) =>
	(trigger === 'blur' || trigger === 'change') &&
	!!blurredField &&
	(blurredField.endsWith('zipCode') || blurredField.endsWith('state'));

// generate a route-aware container component
const ApplicationEntryContainer = withAsyncId(
	withRouter(
		connect(makeMapStateToProps, mapDispatchToProps)(
			reduxForm({
				asyncValidate,
				form: 'ApplicationEntry',
				destroyOnUnmount: false, // keep form data after component unmounts
				forceUnregisterOnUnmount: true,
				shouldAsyncValidate,
			})(toJS(ApplicationEntry)),
		),
	),
);

export default ApplicationEntryContainer;
